import { Fragment, useEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";

/**
 * One player per lesson. React Router keeps a route's element mounted while
 * only its params change, so /vo-demo/A -> /vo-demo/B (Next lesson) would
 * keep A's player and its providers -- runtime clock, event bus, cooldown
 * completion set -- with A's state. A new lesson id unmounts all of it first,
 * as leaving for the list and opening B from there does.
 *
 * Not a bare `key`: React renders B, and runs its layout effects, before A's
 * passive cleanups. VoDemo's unmount cleanup is passive and is what clears the
 * lesson and session ids from the stores, so B's first render would read A's
 * as its own. One empty commit lets A finish leaving before B renders.
 */
export function RemountOnLessonChange({ children }: { children: ReactNode }) {
  const { lessonId } = useParams();
  const [mountedLessonId, setMountedLessonId] = useState(lessonId);

  useEffect(() => {
    setMountedLessonId(lessonId);
  }, [lessonId]);

  if (mountedLessonId !== lessonId) return null;
  return <Fragment key={lessonId}>{children}</Fragment>;
}
